import { agentDataFormat, getFormatRule } from "@/pages/aigc/utils/utils";
import dateTimeFormat from "@home/utils/dbDataFormat/dateTimeFormat";
import { EChartsOption } from "echarts";
import { XAXisOption } from "echarts/types/dist/shared";
import { merge } from 'lodash-es';

/**
 * 设置 yAxis 的 axisLabel.formatter，兼容 yAxis 为数组/对象的情况
 * @param option ECharts 配置项
 * @param dataFormatType 数据格式类型（如 'percent'）
 * @param formatter 格式化规则函数
 * @returns 修改后的 option
 */
export const setYAxisLabelFormatter = (
  option: EChartsOption,
  dataFormatType?: string,
  formatter?: (params: { value: any; formatRule?: any; }) => string | number
): EChartsOption => {
  const { yAxis } = option || {};

  // 处理 yAxis 为数组的情况
  if (Array.isArray(yAxis)) {
    return {
      ...option,
      yAxis: yAxis.map((axis) => {
        // 安全访问 axisLabel，不存在则创建
        const axisLabel = axis?.axisLabel || {};
        return {
          ...axis,
          axisLabel: {
            ...axisLabel,
            formatter: (value: any) => {
              // let val = value;
              // // 如果是 percent 类型，则乘以 100
              // if (dataFormatType === 'percent') {
              //   val = val ? val * 100  : val;
              // }
              // console.log('valvalvalval',val)
              // 使用传入的 formatter 进行格式化
              return formatter ? formatter({ value, formatRule: formatter }) : value;
            },
          },
        };
      }),
    };
  }
  // 处理 yAxis 为单个对象的情况
  if (yAxis) {
    const axisLabel = yAxis?.axisLabel || {};
    return {
      ...option,
      yAxis: {
        ...yAxis,
        axisLabel: {
          ...axisLabel,
          formatter: (value: any) => {
            // let val = value;
            // if (dataFormatType === 'percent') {
            //   val = val ? val * 100: val;
            // }
            // console.log('valvalvalval',val)
            return formatter ? formatter({ value, formatRule: formatter }) : value;
          },
        },
      },
    };
  }

  // 如果 yAxis 不存在或为空，则直接返回原配置
  return option;
};

/**
 * 确定y轴的最大值
 * @param outMaxValue 外部指定的最大值 (优先使用)
 * @param shouldSetMax 是否强制设置最大值
 * @param max 当前计算出的最大值
 * @param maxSetVal 强制设置时的基准值
 * @returns 最终确定的最大值
 */
function determineYAxisMax(outMaxValue: number, shouldSetMax: boolean, max: number, maxSetVal: number, yAxis?: any): number {
  if (outMaxValue) {
    return outMaxValue;
  }
  if (shouldSetMax && max < maxSetVal) {
    return maxSetVal;
  }
  return yAxis?.max ? yAxis?.max : undefined; // 或返回原yAxis.max (需根据上下文调整)
}

// 在 _utls.ts 文件中添加这个方法
export function setYAxisMax(opt: EChartsOption, setMaxValConfig: any, outMaxValue: number,): EChartsOption {
  const { shouldSetMax = false, maxSetVal = 5 } = setMaxValConfig || {};

  if (!opt.yAxis) {
    return {
      ...opt,
      yAxis: {
        max: (value) => {
          const { max } = value || {};
          return determineYAxisMax(outMaxValue, shouldSetMax, max, maxSetVal);
        }
      }
    };
  }

  if (Array.isArray(opt.yAxis)) {
    // 处理多 y 轴情况
    return {
      ...opt,
      yAxis: opt.yAxis.map(yAxis => ({
        ...yAxis,
        max: (value) => {
          const { max } = value || {};
          // const maxVal = determineYAxisMax(outMaxValue, shouldSetMax, max, maxSetVal);
          // return yAxis?.max ? yAxis?.max : undefined;
          return determineYAxisMax(outMaxValue, shouldSetMax, max, maxSetVal, yAxis);
        }
      }))
    };
  } 
  // 处理单 y 轴情况
  return {
    ...opt,
    yAxis: {
      ...opt.yAxis,
      max: (value) => {
        const { max } = value || {};
        if (outMaxValue) {
          return outMaxValue;
        }
        if (shouldSetMax && max < maxSetVal) {
          return maxSetVal;
        }
        return determineYAxisMax(outMaxValue, shouldSetMax, max, maxSetVal, opt.yAxis);
      }
    }
  };
  
}

/**
 * 设置 y 轴 splitNumber
 * @param opt ECharts 配置项
 * @param yAxisFormatter 当前 y 轴格式化类型
 * @param maxValue 数据中的最大值
 * @returns 修改后的 ECharts 配置项
 */
export function setYAxisSplitNumber(
  opt: EChartsOption,
  yAxisFormatter: string,
  maxValue: number
): EChartsOption {
  // 判断条件：yAxisFormatter 不是 percentInt 且最大值小于 5
  const shouldSetSingleSplit = yAxisFormatter !== 'percentInt' && maxValue < 150000000;

  if (!opt.yAxis) {
    return {
      ...opt,
      yAxis: {
        splitNumber: shouldSetSingleSplit ? 1 : 5
      }
    };
  }

  if (Array.isArray(opt.yAxis)) {
    return {
      ...opt,
      yAxis: opt.yAxis.map(yAxis => ({
        ...yAxis,
        splitNumber: shouldSetSingleSplit ? 1 : yAxis.splitNumber
      }))
    };
  } 
  return {
    ...opt,
    yAxis: {
      ...opt.yAxis,
      splitNumber: shouldSetSingleSplit ? 1 : opt.yAxis.splitNumber
    }
  };
  
}

/**
 * 设置 y 轴翻转
 * @param opt ECharts 配置项
 * @param inverse 是否翻转 y 轴，默认为 true
 * @returns 修改后的 ECharts 配置项
 */
export function setYAxisInverse(
  opt: EChartsOption,
  inverse = true
): EChartsOption {
  if (!opt.yAxis) {
    return merge({}, opt, {
      yAxis: {
        inverse
      }
    });
  }

  if (Array.isArray(opt.yAxis)) {
    return merge({}, opt, {
      yAxis: opt.yAxis.map(yAxis => merge({}, yAxis, { inverse }))
    });
  } 
  return merge({}, opt, {
    yAxis: {
      inverse
    }
  });
  
}

/**
 * 折线图 series 数据结构
 */
interface ISeriesData {
  name?: string;
  data?: (number | null | undefined)[];
}

/**
 * 将 lineSeries 数据转换为饼图数据格式
 * 取每个 series 最后一个时间点的值作为饼图扇区值
 * @param lineSeries 折线图数据
 * @returns 饼图数据 [{ name, value }]
 */
export const getPieData = (lineSeries: ISeriesData[]): { name: string; value: number; }[] => {
  if (!lineSeries || lineSeries.length === 0) return [];
  return lineSeries.map((series) => {
    const dataArr = series?.data || [];
    const lastValue = dataArr?.[dataArr.length - 1] ?? 0;
    return {
      name: series?.name || '',
      value: typeof lastValue === 'number' ? lastValue : 0,
    };
  });
};

/**
 * 应用 yAxis 配置到图表选项
 * @param opt ECharts 配置项
 * @param yAxisConfig yAxis 配置对象（包含业务字段 data_key，需要排除）
 * @returns 修改后的 ECharts 配置项
 */
export function applyYAxisConfig(
  opt: EChartsOption,
  yAxisConfig: { data_key?: string; name?: string; yAxis_index?: number; [key: string]: any; }
): EChartsOption {
  if (!yAxisConfig) {
    return opt;
  }

  // 排除业务字段，只保留 ECharts 配置
  const { data_key, name, yAxis_index, ...echartsConfig } = yAxisConfig;

  // 如果没有有效的 ECharts 配置，直接返回
  if (Object.keys(echartsConfig).length === 0) {
    return opt;
  }

  if (!opt.yAxis) {
    return merge({}, opt, {
      yAxis: echartsConfig
    });
  }

  if (Array.isArray(opt.yAxis)) {
    return merge({}, opt, {
      yAxis: opt.yAxis.map(yAxis => merge({}, yAxis, echartsConfig))
    });
  } 
  return merge({}, opt, {
    yAxis: merge({}, opt.yAxis, echartsConfig)
  });
  
}

export const getMaxYValue = (chartData) => {
  if (!chartData || !Array.isArray(chartData)) {
    return 0; // 或根据需求返回 null/undefined
  }
  let maxY = -Infinity;
  chartData.forEach(series => {
    if (series.data && Array.isArray(series.data)) {
      series.data.forEach(point => {
        if (typeof point.y === 'number') {
          maxY = Math.max(maxY, point.y);
        }
      });
    }
  });
  return maxY === -Infinity ? 0 : maxY; // 如果没有有效值返回0，或根据需求调整
};

export const handleAxisFormat = (value: string, xAxisFormat:string, timeType: string, xAxisIsDateOrTime: boolean) => {
  if(xAxisFormat) {
    return agentDataFormat({ value, formatRule: getFormatRule(xAxisFormat), isDate: xAxisIsDateOrTime  });
  }
  if (!xAxisIsDateOrTime) {
    return value;
  }

  if (timeType === "Month") {
    return dateTimeFormat(value, 'month');
  } if (timeType === "hour") {
    return dateTimeFormat(value, 'hour');
  }
  return dateTimeFormat(value, 'mDay');
};

/**
 * 计算字符串的显示宽度（ASCII字符算1个宽度，非ASCII字符算2个宽度）
 * @param str 要计算的字符串
 * @returns 计算后的总宽度
 */
export function calculateStringWidth(str: string): number {
  if (!str) return 0;
  
  let width = 0;
  for (const char of str) {
    width += char.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

// xAxis为外部传入的配置，opt为echarts现有的配置，xAxis可以为字符串或者json
// 当为字符串的时候返回opt
// 当为json的时候，有date_key、show_type、和其他echarts的xAxis配置
// show_type的值有三个，代表x轴的展示方式
// 1:normal 正常展示，echarts默认形式，在此可直接返回opt
// 2:all 全部展示，不自动隐藏标签，不旋转角度
// 3:all_tilt 全部倾斜展示，角度45度
export const setXAxis = (xAxis: XAXisOption, opt:EChartsOption, width: number) => {
  // 如果xAxis是字符串，直接返回现有配置， 如果xAxis不是对象，也直接返回现有配置
  if (typeof xAxis === 'string' || typeof xAxis !== 'object' || xAxis === null) {
    return opt;
  }
  // const maxWidth = 20;
  // 解构出show_type和其他配置
  const { show_type, date_key, ...restConfig } = xAxis;
  // const oldGrid = opt.grid;
  // let newGrid = {};
  // 根据show_type处理不同的xAxis配置
  let xAxisConfig = {};
  if (show_type === 'all') {
    xAxisConfig = {
      axisLabel: {
        interval: 0,  // 显示所有标签
        rotate: 0     // 不旋转
      }
    };
  } else if (show_type === 'all_tilt') {
    xAxisConfig = {
      axisLabel: {
        interval: 0,  // 显示所有标签
        rotate: 45,   // 旋转45度
        margin: 10,    // 增加边距防止重叠
        align: null,
        width: width || 50, // 可根据实际需要调整
        overflow: 'truncate',
        ellipsis: '...'
      }
    };
  }
  const oldOptXAxis = opt?.xAxis;
  let mergedConfig = {};
  // 合并配置：先合并show_type相关的配置，再合并用户传入的其他配置
  if (Array.isArray(oldOptXAxis)) {
    const [firstXAxis, ...otherAxis] = oldOptXAxis || [];
    const newFirstXAxis = merge(
      {},
      firstXAxis,
      xAxisConfig,
      restConfig
    );
    mergedConfig = merge(
      {},
      opt,
      {
        xAxis: [
          newFirstXAxis,
          ...(otherAxis || [])
        ]
      },
      { xAxis: restConfig },
      // { grid: newGrid }
    );
  } else {
    mergedConfig = merge(
      {},
      opt,
      { xAxis: xAxisConfig },
      { xAxis: restConfig },
      // { grid: newGrid }
    );
  }
  return mergedConfig;
};

/**
 * 设置图表 series 的 stack 属性
 * @param opt ECharts 配置项
 * @returns 修改后的 ECharts 配置项
 */
export const setSeriesStack = (opt: EChartsOption): EChartsOption => {
  // 如果是 flat_bar 类型且存在 series，则设置 stack 为空字符串
  const series = Array.isArray(opt.series) ? opt.series : [opt.series];
  return {
    ...opt,
    series: series.map((seriesItem: any) => ({
      ...seriesItem,
      stack: '',
    })),
  };
};
// 是否关闭高亮状态。
export const setSeriesEmphasis = (opt: EChartsOption): EChartsOption => {
  const series = Array.isArray(opt.series) ? opt.series : [opt.series];
  return {
    ...opt,
    axisPointer: {
      ...opt.axisPointer || {}, 
      z: 0,
    },
    series: series.map((seriesItem: any) => ({
      ...seriesItem,
      emphasis: {
        disabled: true,
      },
    })),
  };
};